import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  TextField,
  Checkbox,
  Radio,
  InputAdornment,
  Chip,
} from "@mui/material";
import {
  MergeType as MergeTypeIcon,
  Search as SearchIcon,
  Language as LanguageIcon,
  CloseOutlined,
  DragIndicator as DragIndicatorIcon,
  EditOutlined as EditOutlinedIcon,
  AddOutlined as AddOutlinedIcon,
} from "@mui/icons-material";
import { useAuth } from "context/AuthContext";
import { FAST_API_BASE_URL } from "../../../constants";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useSystemTheme } from "context/SystemThemeContext";


const GroupCard = ({ group, onEdit, isDark }) => {
  const sortedChildren = [...(group.children || [])].sort(
    (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
  );

  return (
    <Box
      sx={{
        border: `1px solid ${isDark ? "#ececec" : "#e8eef0"}`,
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: isDark ? "#060C18" : "#fff",
        boxShadow: isDark
          ? "0 2px 8px rgba(0,0,0,0.25)"
          : "0 2px 8px rgba(33,146,151,0.05)",
        mb: 1.5,
      }}
    >
      <Box
        px={2}
        py={1.25}
        sx={{
          borderBottom: `1px solid ${isDark ? "#919191" : "#f0f4f4"}`,
          background: isDark ? "#0a1020" : "linear-gradient(135deg,#f8fefe,#f2fafa)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            variant="caption"
            fontSize="10px"
            fontWeight={700}
            sx={{
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: isDark ? "#ffffff" : "#aab8b8",
            }}
          >
            Parent
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              backgroundColor: "rgba(33,146,151,0.1)",
              border: "1px solid rgba(33,146,151,0.25)",
              borderRadius: "20px",
              px: 1,
              py: 0.375,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: "#3AAFA9",
                flexShrink: 0,
              }}
            />
            <Typography fontSize="12px" fontWeight={600} color="#3AAFA9">
              {group.parent?.domain_name}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            fontSize="12px"
            sx={{ color: isDark ? "#ffffff" : "#b0c0c0" }}
          >
            →
          </Typography>
          <Typography
            variant="caption"
            fontSize="11px"
            sx={{ color: isDark ? "#ffffff" : "#b0c0c0" }}
          >
            {sortedChildren.length} sub domain{sortedChildren.length !== 1 ? "s" : ""}
          </Typography>
        </Box>

        <IconButton
          size="small"
          onClick={onEdit}
          sx={{
            p: 0.5,
            borderRadius: "7px",
            color: isDark ? "#ffffff" : "#b0c0c0",
            border: `1px solid ${isDark ? "#e2e2e2" : "#e8eef0"}`,
            "&:hover": {
              color: "#3AAFA9",
              borderColor: "rgba(33,146,151,0.35)",
              backgroundColor: "rgba(33,146,151,0.06)",
            },
            transition: "all 0.13s",
          }}
        >
          <EditOutlinedIcon sx={{ fontSize: 13 }} />
        </IconButton>
      </Box>
      <Box
        px={2}
        py={1.25}
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.75,
          alignItems: "center",
          backgroundColor: isDark ? "#060C18" : "#fff",
        }}
      >
        {sortedChildren.map((child, idx) => (
          <Box
            key={child.domain_token}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.625,
              backgroundColor: isDark ? "#0a1020" : "#f5f7f7",
              border: `1px solid ${isDark ? "#1e2535" : "#e0e8e8"}`,
              borderRadius: "20px",
              px: 1,
              py: 0.375,
            }}
          >
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: "5px",
                background: "linear-gradient(135deg,#3AAFA9,#1a7579)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Typography fontSize="10px" fontWeight={700} color="#fff" lineHeight={1}>
                {idx + 1}
              </Typography>
            </Box>
            <LanguageIcon sx={{ fontSize: 11, color: isDark ? "#ffffff" : "#b0c0c0" }} />
            <Typography
              fontSize="12px"
              fontWeight={500}
              sx={{ color: isDark ? "#a0aec0" : "#5a6e6e" }}
            >
              {child.domain_name}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};


const DomainListItem = ({ domain, isSelected, onClick, isDisabled, showCheckbox, disabledReason }) => {
  const { mode } = useSystemTheme();
  const isDark = mode === "dark";

  return (
    <Box
      onClick={!isDisabled ? onClick : undefined}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 1.5,
        py: 0.875,
        cursor: isDisabled ? "not-allowed" : "pointer",
        borderRadius: "8px",
        mb: 0.5,
        opacity: isDisabled ? (disabledReason ? 0.5 : 0.3) : 1,
        backgroundColor: isSelected
          ? isDark ? "rgba(33,146,151,0.15)" : "rgba(33,146,151,0.07)"
          : "transparent",
        border: isSelected
          ? `1px solid ${isDark ? "rgba(33,146,151,0.5)" : "rgba(33,146,151,0.35)"}`
          : "1px solid transparent",
        transition: "all 0.13s ease",
        "&:hover": !isDisabled
          ? {
              backgroundColor: isDark ? "rgba(33,146,151,0.1)" : "rgba(33,146,151,0.04)",
              border: `1px solid ${isDark ? "rgba(33,146,151,0.35)" : "rgba(33,146,151,0.22)"}`,
              transform: "translateX(2px)",
            }
          : {},
      }}
    >
      {showCheckbox ? (
        <Checkbox
          checked={isSelected}
          size="small"
          sx={{
            p: 0,
            color: isDark ? "#ffffff" : "#d0d0d0",
            "&.Mui-checked": { color: "#3AAFA9" },
            "& .MuiSvgIcon-root": { fontSize: 16 },
          }}
        />
      ) : (
        <Radio
          checked={isSelected}
          size="small"
          sx={{
            p: 0,
            color: isDark ? "#ffffff" : "#d0d0d0",
            "&.Mui-checked": { color: "#3AAFA9" },
            "& .MuiSvgIcon-root": { fontSize: 16 },
          }}
        />
      )}
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: "6px",
          backgroundColor: isSelected
            ? "rgba(33,146,151,0.15)"
            : isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <LanguageIcon
          sx={{
            fontSize: 12,
            color: isSelected ? "#3AAFA9" : isDark ? "#ffffff" : "#bbb",
          }}
        />
      </Box>
      <Typography
        variant="body2"
        fontSize="12.5px"
        fontWeight={isSelected ? 600 : 400}
        // FIX: Change color for light mode
        color={
          isSelected 
            ? "#3AAFA9" 
            : isDark 
              ? "#e2e8f0" 
              : "#344767"  // Changed from "#ffffff" to "#344767"
        }
        noWrap
        sx={{ flex: 1, letterSpacing: "-0.01em" }}
      >
        {domain.domain_name}
      </Typography>

      {/* Show "in use" badge when disabled because it's already a parent */}
      {isDisabled && disabledReason && (
        <Typography
          variant="caption"
          fontSize="10px"
          sx={{
            flexShrink: 0,
            color: isDark ? "#ffffff" : "#b0c0c0",
            fontStyle: "italic",
            letterSpacing: "0.01em",
          }}
        >
          in use
        </Typography>
      )}
    </Box>
  );
};
// ─── ListPanel ────────────────────────────────────────────────────────────────

const ListPanel = ({ title, subtitle, search, onSearch, items, renderItem }) => {
  const { mode } = useSystemTheme();
  const isDark = mode === "dark";

  return (
    <Box
      sx={{
        border: `1px solid ${isDark ? "#1e2535" : "#e8eef0"}`,
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: isDark ? "#060C18" : "#fff",
        boxShadow: isDark
          ? "0 2px 8px rgba(0,0,0,0.25)"
          : "0 2px 8px rgba(33,146,151,0.05)",
      }}
    >
      <Box
        px={2}
        py={1.5}
        sx={{
          borderBottom: `1px solid ${isDark ? "#1e2535" : "#f0f4f4"}`,
          background: isDark ? "#0a1020" : "linear-gradient(135deg,#f8fefe,#f2fafa)",
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            variant="caption"
            fontWeight={800}
            sx={{
              textTransform: "uppercase",
              letterSpacing: "0.09em",
              fontSize: "10px",
              color: "#3AAFA9",
            }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              height: "1px",
              flex: 1,
              background: isDark
                ? "linear-gradient(to right,rgba(33,146,151,0.3),transparent)"
                : "linear-gradient(to right,rgba(33,146,151,0.2),transparent)",
            }}
          />
        </Box>
        <Typography
          variant="caption"
          display="block"
          mt={0.25}
          fontSize="11px"
          sx={{ color: isDark ? "#a0aec0" : "#aab8b8" }}
        >
          {subtitle}
        </Typography>
      </Box>

      <Box
        px={1.5}
        py={1}
        sx={{
          borderBottom: `1px solid ${isDark ? "#1e2535" : "#f5f5f5"}`,
          backgroundColor: isDark ? "#0a1020" : "#fdfefe",
        }}
      >
        <TextField
          size="small"
          fullWidth
          placeholder="Search domains…"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 13, color: isDark ? "#ffffff" : "#b0c4c4" }} />
              </InputAdornment>
            ),
            sx: {
              fontSize: "12.5px",
              borderRadius: "8px",
              backgroundColor: isDark ? "#1a2035" : "#fff",
              color: isDark ? "#e2e8f0" : "#344767", // Added light mode text color
              "& fieldset": { borderColor: isDark ? "#2d3748" : "#eaeaea" },
              "&:hover fieldset": { borderColor: "#3AAFA9 !important" },
              "&.Mui-focused fieldset": {
                borderColor: "#3AAFA9 !important",
                borderWidth: "1.5px",
              },
            },
          }}
          inputProps={{
            style: {
              fontSize: "12.5px",
              padding: "6px 8px",
              color: isDark ? "#e2e8f0" : "#344767", // Added light mode text color
            },
          }}
        />
      </Box>

      <Box
        sx={{
          maxHeight: 230,
          overflowY: "auto",
          px: 1,
          py: 0.75,
          "&::-webkit-scrollbar": { width: 3 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(33,146,151,0.2)",
            borderRadius: 2,
          },
        }}
      >
        {items.length === 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={1}>
            <LanguageIcon sx={{ color: isDark ? "#2d3748" : "#ddd", fontSize: 26 }} />
            <Typography variant="caption" fontSize="11px" sx={{ color: isDark ? "#ffffff" : "#ccc" }}>
              No domains found
            </Typography>
          </Box>
        ) : (
          items.map(renderItem)
        )}
      </Box>
    </Box>
  );
};

// ─── RankItem ─────────────────────────────────────────────────────────────────

const RankItem = ({
  domain, rank, isDark, onRemove,
  onDragStart, onDragOver, onDrop, onDragEnd,
  isDraggingOver, isDragging,
}) => (
  <Box
    draggable
    onDragStart={onDragStart}
    onDragOver={onDragOver}
    onDrop={onDrop}
    onDragEnd={onDragEnd}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      px: 1.5,
      py: 1,
      mb: 0.75,
      borderRadius: "9px",
      cursor: "grab",
      userSelect: "none",
      border: isDraggingOver
        ? "1px solid #3AAFA9"
        : `1px solid ${isDark ? "#1e2535" : "#edf0f2"}`,
      backgroundColor: isDark
        ? isDraggingOver ? "rgba(33,146,151,0.12)" : "#0a1020"
        : isDraggingOver ? "rgba(33,146,151,0.05)" : "#fff",
      opacity: isDragging ? 0.4 : 1,
      transform: isDraggingOver ? "scale(1.01)" : "scale(1)",
      boxShadow: isDraggingOver
        ? "0 0 0 2px rgba(33,146,151,0.2)"
        : isDark ? "0 1px 3px rgba(0,0,0,0.2)" : "0 1px 3px rgba(0,0,0,0.06)",
      transition: "all 0.12s ease",
      "&:last-child": { mb: 0 },
      "&:active": { cursor: "grabbing" },
    }}
  >
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: "6px",
        background: "linear-gradient(135deg,#3AAFA9,#1a7579)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        boxShadow: "0 2px 4px rgba(33,146,151,0.3)",
      }}
    >
      <Typography fontSize="11px" fontWeight={700} color="#fff" lineHeight={1}>
        {rank}
      </Typography>
    </Box>
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: "5px",
        backgroundColor: "rgba(33,146,151,0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <LanguageIcon sx={{ fontSize: 11, color: "#3AAFA9" }} />
    </Box>
    <Typography
      variant="caption"
      fontSize="12.5px"
      fontWeight={500}
      noWrap
      flex={1}
      sx={{ color: isDark ? "#e2e8f0" : "#344767", letterSpacing: "-0.01em" }}
    >
      {domain.domain_name}
    </Typography>
    <DragIndicatorIcon
      sx={{ fontSize: 16, color: isDark ? "#2d3748" : "#d0d8d8", flexShrink: 0, mr: 0.25 }}
    />
    <IconButton
      size="small"
      onClick={onRemove}
      sx={{
        p: 0.4,
        borderRadius: "6px",
        color: isDark ? "#ffffff" : "#ccc",
        "&:hover": { color: "#e74c3c", backgroundColor: "rgba(231,76,60,0.08)" },
        transition: "all 0.12s",
      }}
    >
      <CloseOutlined sx={{ fontSize: 13 }} />
    </IconButton>
  </Box>
);

// ─── MergeDomainsSection ──────────────────────────────────────────────────────

const MergeDomainsSection = () => {
  const { mode } = useSystemTheme();
  const isDark = mode === "dark";

  const [domains, setDomains]               = useState([]);
  const [mergedGroups, setMergedGroups]     = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [parentDomain, setParentDomain]     = useState(null);
  const [childDomains, setChildDomains]     = useState([]);  // order = priority
  const [leftSearch, setLeftSearch]         = useState("");
  const [rightSearch, setRightSearch]       = useState("");
  const [merging, setMerging]               = useState(false);
  const [mergeError, setMergeError]         = useState("");
  const [mergeSuccess, setMergeSuccess]     = useState("");
  // null = create mode, number = index of group being edited
  const [editingGroupIdx, setEditingGroupIdx] = useState(null);

  const draggingIdxRef = useRef(null);
  const [dragOverIdx, setDragOverIdx]   = useState(null);
  const [draggingIdx, setDraggingIdx]   = useState(null);

  // tokens already used as a parent in any saved group
  const existingParentTokens = useMemo(
    () => new Set(mergedGroups.map(g => g.parent?.domain_token)),
    [mergedGroups]
  );

  // parent token of the group currently being edited — stays selectable
  const editingParentToken = useMemo(
    () => editingGroupIdx !== null ? mergedGroups[editingGroupIdx]?.parent?.domain_token : null,
    [mergedGroups, editingGroupIdx]
  );

  const { user }    = useAuth();
  const { userId }  = useParams();
  const targetUserId = userId || user?.id;

  useEffect(() => { fetchDomains(); }, []);

  const fetchDomains = async () => {
    setDomainsLoading(true);
    try {
      const res = await axios.get(
        `${FAST_API_BASE_URL}/api/users/${targetUserId}/merged-domain-details`,
        // `http://localhost:8000/api/users/${targetUserId}/merged-domain-details`,
        { headers: { "x-api-key": process.env.REACT_APP_API_KEY } }
      );
      if (res.data?.resultCode === "0" && res.data?.result) {
        const { domains: fetchedDomains = [], merged_groups = [] } = res.data.result;
        setDomains(fetchedDomains);
        setMergedGroups(merged_groups);
      }
    } catch (err) {
      console.error("Failed to fetch domains:", err);
      setMergeError("Failed to load domains. Please refresh.");
    } finally {
      setDomainsLoading(false);
    }
  };

  const handleEditGroup = useCallback((groupIdx) => {
    const group = mergedGroups[groupIdx];

    const parentObj = domains.find(d => d.domain_token === group.parent?.domain_token);
    const childObjs = [...(group.children || [])]
      .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
      .map(c => domains.find(d => d.domain_token === c.domain_token))
      .filter(Boolean);

    setParentDomain(parentObj || null);
    setChildDomains(childObjs);
    setEditingGroupIdx(groupIdx);
    setMergeError("");
    setMergeSuccess("");

    document.getElementById("merge-form-section")?.scrollIntoView({
      behavior: "smooth", block: "start",
    });
  }, [mergedGroups, domains]);

  const handleCancelEdit = useCallback(() => {
    setEditingGroupIdx(null);
    setParentDomain(null);
    setChildDomains([]);
    setMergeError("");
  }, []);

  const handleSelectParent = useCallback((domain) => {
    if (parentDomain?.domain_token === domain.domain_token) {
      setParentDomain(null);
      return;
    }
    setParentDomain(domain);
    setChildDomains(prev => prev.filter(d => d.domain_token !== domain.domain_token));
  }, [parentDomain]);

  const handleToggleChild = useCallback((domain) => {
    if (parentDomain?.domain_token === domain.domain_token) return;
    setChildDomains(prev => {
      const exists = prev.find(d => d.domain_token === domain.domain_token);
      if (exists) return prev.filter(d => d.domain_token !== domain.domain_token);
      return [...prev, domain];
    });
  }, [parentDomain]);

  const handleRemoveChild = useCallback((token) => {
    setChildDomains(prev => prev.filter(d => d.domain_token !== token));
  }, []);

  const handleDragStart = useCallback((e, idx) => {
    draggingIdxRef.current = idx;
    setDraggingIdx(idx);
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDragOver = useCallback((e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIdx(idx);
  }, []);

  const handleDrop = useCallback((e, idx) => {
    e.preventDefault();
    const from = draggingIdxRef.current;
    if (from === null || from === idx) return;
    setChildDomains(prev => {
      const arr = [...prev];
      const [moved] = arr.splice(from, 1);
      arr.splice(idx, 0, moved);
      return arr;
    });
    draggingIdxRef.current = null;
    setDraggingIdx(null);
    setDragOverIdx(null);
  }, []);

  const handleDragEnd = useCallback(() => {
    draggingIdxRef.current = null;
    setDraggingIdx(null);
    setDragOverIdx(null);
  }, []);

  const buildFallbackOrders = useCallback(() => {
    const allSelected = [parentDomain, ...childDomains];
    return allSelected.map(src => ({
      source_token: src.domain_token,
      targets: allSelected
        .filter(d => d.domain_token !== src.domain_token)
        .map((d, i) => ({ token: d.domain_token, priority: i + 1 })),
    }));
  }, [parentDomain, childDomains]);

const handleMerge = async () => {
  if (!parentDomain) {
    setMergeError("Please select a parent domain.");
    return;
  }

  if (!childDomains.length) {
    setMergeError("Please select at least one child domain.");
    return;
  }

  setMerging(true);
  setMergeError("");
  setMergeSuccess("");

  try {
    const payload = {
      user_id: Number(targetUserId),
      mappings: childDomains.map((domain, index) => ({
        parent_domain_id: parentDomain.domain_token,
        child_domain_id: domain.domain_token,
        priority: index + 1,
      })),
    };

    const isExistingGroup =
      editingGroupIdx !== null ||
      mergedGroups.some(
        (g) => g.parent?.domain_token === parentDomain.domain_token
      );

    const config = {
      headers: {
        "x-api-key": process.env.REACT_APP_API_KEY,
      },
    };

    // const url = `http://localhost:8000/api/merge-website-collections`;
    const url = `${FAST_API_BASE_URL}/api/merge-website-collections`;

    const res = isExistingGroup
      ? await axios.put(url, payload, config)
      : await axios.post(url, payload, config);

    if (res.data?.resultType === "Success" || res.status === 200) {
      setMergeSuccess(
        isExistingGroup
          ? "Group updated successfully."
          : `Successfully merged ${childDomains.length} domain(s) under "${parentDomain.domain_name}".`
      );

      setParentDomain(null);
      setChildDomains([]);
      setEditingGroupIdx(null);

      fetchDomains();
    } else {
      setMergeError(res.data?.message || "Merge failed. Please try again.");
    }
  } catch (err) {
    setMergeError(
      err.response?.data?.message || "Merge failed. Please try again."
    );
  } finally {
    setMerging(false);
  }
};

  const filteredLeft = useMemo(
    () => domains.filter(d => d.domain_name?.toLowerCase().includes(leftSearch.toLowerCase())),
    [domains, leftSearch]
  );
  const filteredRight = useMemo(
    () => domains.filter(d => d.domain_name?.toLowerCase().includes(rightSearch.toLowerCase())),
    [domains, rightSearch]
  );

  const isReady = parentDomain && childDomains.length > 0;
  const isEditMode = editingGroupIdx !== null;

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Box mt={3}>

        {mergedGroups.length > 0 && (
          <Box mb={3}>
            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
              <Typography
                variant="caption"
                fontWeight={800}
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: "0.09em",
                  fontSize: "10px",
                  color: isDark ? "#ffffff" : "#b0c0c0",
                }}
              >
                Existing merged groups
              </Typography>
              <Chip
                label={mergedGroups.length}
                size="small"
                sx={{
                  height: 17,
                  fontSize: "10px",
                  fontWeight: 700,
                  backgroundColor: isDark ? "rgba(33,146,151,0.15)" : "rgba(33,146,151,0.1)",
                  color: "#3AAFA9",
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            </Box>

            {mergedGroups.map((group, idx) => (
              <GroupCard
                key={`${group.parent?.domain_token}-${idx}`}
                group={group}
                isDark={isDark}
                onEdit={() => handleEditGroup(idx)}
              />
            ))}
          </Box>
        )}

        <Box
          id="merge-form-section"
          sx={{
            border: `1px solid ${isDark ? "#fcfdff" : "#e2eaea"}`,
            borderRadius: "16px",
            backgroundColor: isDark ? "#060C18" : "#fff",
            overflow: "hidden",
            boxShadow: isDark
              ? "0 4px 24px rgba(0,0,0,0.35)"
              : "0 4px 24px rgba(33,146,151,0.08)",
            transition: "background-color 0.3s, border-color 0.3s",
            outline: isEditMode ? "2px solid rgba(33,146,151,0.35)" : "none",
            outlineOffset: "2px",
          }}
        >
          <Box
            px={3}
            pt={2.5}
            pb={2}
            sx={{
              borderBottom: `1px solid ${isDark ? "#1e2535" : "#edf4f4"}`,
              background: isDark
                ? "#0a1020"
                : "linear-gradient(135deg,#f5fdfd,#edf8f8,#f8fefe)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: -20, right: -20,
                width: 110, height: 110,
                borderRadius: "50%",
                background: "radial-gradient(circle,rgba(33,146,151,0.08) 0%,transparent 70%)",
                pointerEvents: "none",
              },
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={1.5}>
                <Box
                  sx={{
                    width: 34, height: 34,
                    borderRadius: "10px",
                    background: isEditMode
                      ? "#219297"
                      : "linear-gradient(135deg,#3AAFA9,#1a7579)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: isEditMode
                      ? "0 4px 12px rgba(18, 243, 232, 0.3)"
                      : "0 4px 12px rgba(33,146,151,0.3)",
                    flexShrink: 0,
                    transition: "all 0.25s ease",
                  }}
                >
                  {isEditMode
                    ? <EditOutlinedIcon sx={{ color: "#fff", fontSize: 16 }} />
                    : <MergeTypeIcon sx={{ color: "#fff", fontSize: 17 }} />
                  }
                </Box>
                <Box>
                  <Typography
                    fontWeight={700}
                    fontSize="14.5px"
                    letterSpacing="-0.02em"
                    sx={{ color: isDark ? "#e2e8f0" : "#1a2e2e" }}
                  >
                    {isEditMode
                      ? `Edit Group — ${mergedGroups[editingGroupIdx]?.parent?.domain_name}`
                      : "Create Merge Group"
                    }
                  </Typography>
                  <Typography
                    variant="caption"
                    fontSize="11px"
                    sx={{ color: isDark ? "#a0aec0" : "#9db8b8" }}
                  >
                    {isEditMode
                      ? "Modify the parent, children, or priority order"
                      : "Select a parent, pick children, then drag to set priority order"
                    }
                  </Typography>
                </Box>
              </Box>

              {isEditMode && (
                <Button
                  size="small"
                  startIcon={<AddOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                  onClick={handleCancelEdit}
                  sx={{
                    color: "#3AAFA9",
                    fontSize: "12px",
                    fontWeight: 600,
                    textTransform: "none",
                    border: "1px solid rgba(33,146,151,0.3)",
                    borderRadius: "8px",
                    px: 1.5,
                    py: 0.625,
                    "&:hover": {
                      backgroundColor: "rgba(33,146,151,0.06)",
                      borderColor: "rgba(33,146,151,0.5)",
                    },
                  }}
                >
                  New group
                </Button>
              )}
            </Box>
          </Box>

          <Box px={3} py={2.5}>
            {mergeSuccess && (
              <Alert
                severity="success"
                onClose={() => setMergeSuccess("")}
                sx={{
                  mb: 2, py: 0.75, fontSize: "13px", borderRadius: "10px",
                  border: "1px solid rgba(33,146,151,0.2)",
                  backgroundColor: isDark ? "#0a1020" : "rgba(33,146,151,0.05)",
                  color: isDark ? "#e2e8f0" : "#1a5a5a",
                  "& .MuiAlert-icon": { color: "#3AAFA9" },
                }}
              >
                {mergeSuccess}
              </Alert>
            )}
            {mergeError && (
              <Alert
                severity="error"
                onClose={() => setMergeError("")}
                sx={{ mb: 2, py: 0.75, fontSize: "13px", borderRadius: "10px" }}
              >
                {mergeError}
              </Alert>
            )}

            {domainsLoading ? (
              <Box display="flex" flexDirection="column" alignItems="center" py={6} gap={2}>
                <CircularProgress size={24} sx={{ color: "#3AAFA9" }} />
                <Typography variant="caption" fontSize="12px" sx={{ color: isDark ? "#a0aec0" : "#b0c4c4" }}>
                  Loading domains…
                </Typography>
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 48px 1fr",
                    alignItems: "start",
                  }}
                >
                  <ListPanel
                    title="Parent Domain"
                    subtitle="Select one domain"
                    search={leftSearch}
                    onSearch={setLeftSearch}
                    items={filteredLeft}
                    renderItem={(domain) => {
                      const isAlreadyParent =
                        existingParentTokens.has(domain.domain_token) &&
                        domain.domain_token !== editingParentToken;
                      return (
                        <DomainListItem
                          key={domain.domain_token}
                          domain={domain}
                          isSelected={parentDomain?.domain_token === domain.domain_token}
                          onClick={() => handleSelectParent(domain)}
                          isDisabled={isAlreadyParent}
                          disabledReason={isAlreadyParent ? "already a parent" : undefined}
                          showCheckbox={false}
                        />
                      );
                    }}
                  />
                  <Box display="flex" alignItems="flex-start" justifyContent="center" pt="68px">
                    <Box
                      sx={{
                        width: 36, height: 36,
                        borderRadius: "50%",
                        background: isReady
                          ? "linear-gradient(135deg,#3AAFA9,#1a7579)"
                          : isDark ? "#1a2035" : "#f0f4f4",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        transition: "all 0.25s ease",
                        boxShadow: isReady ? "0 4px 14px rgba(33,146,151,0.35)" : "none",
                      }}
                    >
                      <MergeTypeIcon
                        sx={{
                          fontSize: 18,
                          color: isReady ? "#fff" : isDark ? "#2d3748" : "#ccc",
                          transition: "color 0.25s",
                        }}
                      />
                    </Box>
                  </Box>

                  <ListPanel
                    title="Child Domains"
                    subtitle="Select one or more"
                    search={rightSearch}
                    onSearch={setRightSearch}
                    items={filteredRight}
                    renderItem={(domain) => {
                      const isParent = parentDomain?.domain_token === domain.domain_token;
                      return (
                        <DomainListItem
                          key={domain.domain_token}
                          domain={domain}
                          isSelected={!!childDomains.find(d => d.domain_token === domain.domain_token)}
                          onClick={() => handleToggleChild(domain)}
                          isDisabled={isParent}
                          showCheckbox
                        />
                      );
                    }}
                  />
                </Box>
                {childDomains.length > 0 && (
                  <Box
                    mt={2}
                    sx={{
                      border: `1px solid ${isDark ? "rgba(33,146,151,0.22)" : "rgba(33,146,151,0.15)"}`,
                      borderRadius: "12px",
                      overflow: "hidden",
                      boxShadow: isDark
                        ? "0 2px 8px rgba(0,0,0,0.2)"
                        : "0 2px 8px rgba(33,146,151,0.06)",
                    }}
                  >
                    <Box
                      px={2} py={1.25}
                      sx={{
                        borderBottom: `1px solid ${isDark ? "rgba(33,146,151,0.18)" : "rgba(33,146,151,0.1)"}`,
                        background: isDark ? "#0a1020" : "linear-gradient(135deg,#fafefe,#f5fbfb)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                          variant="caption"
                          fontWeight={800}
                          sx={{
                            textTransform: "uppercase",
                            letterSpacing: "0.09em",
                            fontSize: "10px",
                            color: "#3AAFA9",
                          }}
                        >
                          Priority Order
                        </Typography>
                        <Chip
                          label={childDomains.length}
                          size="small"
                          sx={{
                            height: 17,
                            fontSize: "10px",
                            fontWeight: 700,
                            backgroundColor: "#3AAFA9",
                            color: "#fff",
                            "& .MuiChip-label": { px: 0.75 },
                          }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        fontSize="11px"
                        sx={{ color: isDark ? "#a8adb6" : "#b0c0c0" }}
                      >
                        Drag rows to reorder · 1 = highest priority
                      </Typography>
                    </Box>

                    <Box px={1.5} py={1.25} sx={{ background: isDark ? "#060C18" : "#fff" }}>
                      {childDomains.map((d, idx) => (
                        <RankItem
                          key={d.domain_token}
                          domain={d}
                          rank={idx + 1}
                          isDark={isDark}
                          isDraggingOver={dragOverIdx === idx && draggingIdx !== idx}
                          isDragging={draggingIdx === idx}
                          onRemove={() => handleRemoveChild(d.domain_token)}
                          onDragStart={(e) => handleDragStart(e, idx)}
                          onDragOver={(e) => handleDragOver(e, idx)}
                          onDrop={(e) => handleDrop(e, idx)}
                          onDragEnd={handleDragEnd}
                        />
                      ))}
                    </Box>

                    {parentDomain && (
                      <Box
                        px={2} py={1.25}
                        sx={{
                          borderTop: `1px solid ${isDark ? "rgba(33,146,151,0.12)" : "rgba(33,146,151,0.08)"}`,
                          background: isDark
                            ? "#0a1020"
                            : "linear-gradient(135deg,#fafefe,#f5fbfb)",
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 0.75,
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontSize="11px"
                          sx={{ color: isDark ? "#ffffff" : "#b0c0c0", mr: 0.5 }}
                        >
                          Will merge:
                        </Typography>
                        <Chip
                          label={`Parent: ${parentDomain.domain_name}`}
                          size="small"
                          icon={<LanguageIcon style={{ fontSize: 10, color: "#3AAFA9" }} />}
                          sx={{
                            height: 20,
                            fontSize: "10.5px",
                            fontWeight: 600,
                            backgroundColor: isDark ? "rgba(33,146,151,0.15)" : "rgba(33,146,151,0.08)",
                            color: "#3AAFA9",
                            border: "1px solid rgba(33,146,151,0.25)",
                            "& .MuiChip-label": { px: 0.75 },
                            "& .MuiChip-icon": { ml: 0.5 },
                          }}
                        />
                        <Typography
                          variant="caption"
                          fontSize="11px"
                          sx={{ color: isDark ? "#ffffff" : "#b0c0c0" }}
                        >
                          ←
                        </Typography>
                        {childDomains.map((d, i) => (
                          <Chip
                            key={d.domain_token}
                            label={`#${i + 1} ${d.domain_name}`}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "10.5px",
                              fontWeight: 500,
                              backgroundColor: isDark ? "#1a2035" : "#f5f7f7",
                              color: isDark ? "#a0aec0" : "#6b8080",
                              border: `1px solid ${isDark ? "#2d3748" : "#e0e8e8"}`,
                              "& .MuiChip-label": { px: 0.75 },
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
          <Box
            px={3} py={2}
            sx={{
              borderTop: `1px solid ${isDark ? "#1e2535" : "#edf4f4"}`,
              background: isDark ? "#0a1020" : "linear-gradient(135deg,#fafefe,#f5fbfb)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              variant="caption"
              fontSize="11.5px"
              sx={{ color: isDark ? "#ffffff" : "#b0c0c0" }}
            >
              {!parentDomain
                ? "Select a parent domain"
                : !childDomains.length
                  ? "Select at least one child domain"
                  : `${childDomains.length} sub domain${childDomains.length > 1 ? "s" : ""} · drag to reorder`
              }
            </Typography>

            <Box display="flex" gap={1.5} alignItems="center">
              {isEditMode && (
                <Button
                  size="small"
                  onClick={handleCancelEdit}
                  sx={{
                    color: isDark ? "#a0aec0" : "#6b8080",
                    fontSize: "12.5px",
                    fontWeight: 500,
                    textTransform: "none",
                    px: 1.5,
                    "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)" },
                  }}
                >
                  Cancel
                </Button>
              )}
              <Button
                variant="contained"
                size="small"
                startIcon={
                  merging
                    ? <CircularProgress size={13} sx={{ color: "#fff" }} />
                    : isEditMode
                      ? ""
                      : <MergeTypeIcon sx={{ fontSize: "15px !important" }} />
                }
                onClick={handleMerge}
                disabled={merging || !isReady}
                sx={{
                  background: isEditMode
                    ? "#219297"
                    : "linear-gradient(135deg,#3AAFA9,#1a7579)",
                  "&:hover": {
                    background: isEditMode
                      ? "#1b777c"
                      : "linear-gradient(135deg,#1d8388,#156568)",
                    boxShadow: isEditMode
                      ? "0 4px 14px rgba(18, 243, 243, 0.4)"
                      : "0 4px 14px rgba(33,146,151,0.4)",
                    transform: "translateY(-1px)",
                  },
                  "&:active": { transform: "translateY(0)" },
                  "&.Mui-disabled": {
                    background: isDark ? "#1a2035" : "#eaeaea",
                    color: isDark ? "#2d3748" : "#bbb",
                    boxShadow: "none",
                    transform: "none",
                  },
                  color: "#fff",
                  fontSize: "13px",
                  fontWeight: 600,
                  textTransform: "none",
                  px: 2.5,
                  py: 0.875,
                  boxShadow: isEditMode
                    ? "0 2px 8px rgba(18, 243, 232, 0.25)"
                    : "0 2px 8px rgba(33,146,151,0.25)",
                  borderRadius: "9px",
                  letterSpacing: "0.01em",
                  transition: "all 0.2s ease",
                }}
              >
                {merging ? "Saving…" : isEditMode ? "Save Changes" : "Merge Domains"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default MergeDomainsSection;